
import React from 'react'
import { mdx } from '@mdx-js/react'

/* @jsxRuntime classic */
/* @jsx mdx */
import { Actions, ButtonLink, IconArrow } from 'braid-design-system';
import { Recommended } from '../../../components/Recommended/Recommended';


const layoutProps = {
  
};
const MDXLayout = "wrapper"
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">
    <h1 {...{
      "id": "phase-2-apply-with-seek"
    }}>{`Phase 2: Apply with SEEK`}</h1>
    <p>{`This guide is for partners upgrading from the previous version of Apply with SEEK.
New implementations should instead refer to the `}<a parentName="p" {...{
        "href": "/use-cases/apply-with-seek"
      }}>{`use case documentation`}</a>{`.`}</p>
    <p>{`The new Apply with SEEK offers a similar user experience to `}<a parentName="p" {...{
        "href": "https://devportal.seek.com.au/apply-with-seek/overview"
      }}>{`its predecessor`}</a>{`,
though candidates will benefit from the following enhancements:`}</p>
    <ul>
      <li parentName="ul">
        <p parentName="li">{`Richer SEEK Profile information available for pre-fill`}</p>
      </li>
      <li parentName="ul">
        <p parentName="li">{`A familiar, localised experience across our SEEK, Jobstreet, and Jobsdb brands`}</p>
      </li>
      <li parentName="ul">
        <p parentName="li">{`Forward compatibility with a streamlined `}<a parentName="p" {...{
            "href": "/use-cases/apply-with-seek#candidate-experience"
          }}>{`pre-authorization flow`}</a></p>
      </li>
    </ul>
    <p>{`From an integration partner perspective,
the new Apply with SEEK brings:`}</p>
    <ul>
      <li parentName="ul">
        <p parentName="li">{`Common `}<a parentName="p" {...{
            "href": "/introduction#technology"
          }}>{`technology`}</a>{` that enables your software to consolidate its `}<a parentName="p" {...{
            "href": "/auth/partner-tokens"
          }}>{`auth`}</a>{` and access patterns`}</p>
      </li>
      <li parentName="ul">
        <p parentName="li">{`Richer SEEK Profile information via the same GraphQL `}<a parentName="p" {...{
            "href": "/schema/query/candidateProfile"
          }}><inlineCode parentName="a">{`candidateProfile`}</inlineCode>{` query`}</a>{` as `}<a parentName="p" {...{
            "href": "/use-cases/application-export"
          }}>{`Optimised Apply`}</a></p>
      </li>
      <li parentName="ul">
        <p parentName="li">{`Support and localisation across our `}<a parentName="p" {...{
            "href": "/introduction#multi-market-support"
          }}>{`Asia-Pacific markets`}</a></p>
      </li>
    </ul>
    <p>{`To upgrade to the new version of Apply with SEEK,
first review its `}<a parentName="p" {...{
        "href": "/use-cases/apply-with-seek"
      }}>{`use case documentation`}</a>{`:`}</p>

    <Actions mdxType="Actions">
  <ButtonLink href="/use-cases/apply-with-seek" icon={<IconArrow direction="right" mdxType="IconArrow" />} variant="soft" mdxType="ButtonLink">
    Apply with SEEK
  </ButtonLink>
    </Actions>
    <p>{`Then, cover the following migration considerations.`}</p>
    <h2 {...{
      "id": "feature-comparison"
    }}>{`Feature comparison`}</h2>
    <p>{`The table below outlines the key differences between the previous and new versions of Apply with SEEK:`}</p>
    <table>
      <thead parentName="table">
        <tr parentName="thead">
          <th parentName="tr" {...{
            "align": "left"
          }}><p parentName="th"><strong parentName="p">{`Step`}</strong></p></th>
          <th parentName="tr" {...{
            "align": "left"
          }}><p parentName="th"><strong parentName="p">{`Previous version`}</strong></p></th>
          <th parentName="tr" {...{
            "align": "left"
          }}><p parentName="th"><strong parentName="p">{`New version`}</strong></p></th>
        </tr>
      </thead>
      <tbody parentName="table">
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": "left"
          }}><p parentName="td"><a parentName="p" {...{
                "href": "/auth/partner-tokens"
              }}>{`Auth`}</a></p></td>
          <td parentName="tr" {...{
            "align": "left"
          }}><p parentName="td">{`Legacy client credentials`}</p></td>
          <td parentName="tr" {...{
            "align": "left"
          }}><p parentName="td">{`SEEK API `}<a parentName="p" {...{
                "href": "/auth/partner-tokens"
              }}>{`partner access tokens`}</a></p></td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": "left"
          }}><p parentName="td"><a parentName="p" {...{
                "href": "/use-cases/apply-with-seek/display-button#scenario-1-candidate-arrives-from-seek-without-pre-authorization"
              }}>{`Track application state`}</a></p></td>
          <td parentName="tr" {...{
            "align": "left"
          }}><p parentName="td">{`No support`}</p></td>
          <td parentName="tr" {...{
            "align": "left"
          }}><p parentName="td"><inlineCode parentName="p">{`seek-token`}</inlineCode>{` parameter included in link-out redirect`}</p></td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": "left"
          }}><p parentName="td"><a parentName="p" {...{
                "href": "/use-cases/apply-with-seek/display-button"
              }}>{`Display button`}</a></p></td>
          <td parentName="tr" {...{
            "align": "left"
          }}><p parentName="td">{`Link generated by partner`}</p></td>
          <td parentName="tr" {...{
            "align": "left"
          }}><p parentName="td">{`GraphQL `}<a parentName="p" {...{
                "href": "/schema/query/applyWithSeekButton"
              }}><inlineCode parentName="a">{`applyWithSeekButton`}</inlineCode>{` query`}</a>{` returns localised button image, label, and link`}</p></td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": "left"
          }}><p parentName="td"><a parentName="p" {...{
                "href": "/use-cases/apply-with-seek/handle-authorization"
              }}>{`Handle authorization`}</a></p></td>
          <td parentName="tr" {...{
            "align": "left"
          }}><p parentName="td">{`OAuth 2.0 with authorization code exchanged for access token`}</p></td>
          <td parentName="tr" {...{
            "align": "left"
          }}><p parentName="td">{`Revised authorization flow returns `}<inlineCode parentName="p">{`seek-prefill-id`}</inlineCode>{` to retrieve SEEK Profile and `}<inlineCode parentName="p">{`seek-token`}</inlineCode>{` to track application state`}</p></td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": "left"
          }}><p parentName="td"><a parentName="p" {...{
                "href": "/use-cases/apply-with-seek/retrieve-seek-profile"
              }}>{`Retrieve SEEK Profile`}</a></p></td>
          <td parentName="tr" {...{
            "align": "left"
          }}><p parentName="td">{`POST endpoint`}</p></td>
          <td parentName="tr" {...{
            "align": "left"
          }}><p parentName="td">{`GraphQL `}<a parentName="p" {...{
                "href": "/schema/query/candidateProfile"
              }}><inlineCode parentName="a">{`candidateProfile`}</inlineCode>{` query`}</a>{` returns richer information`}</p></td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": "left"
          }}><p parentName="td"><a parentName="p" {...{
                "href": "/use-cases/apply-with-seek/download-resume"
              }}>{`Download resume`}</a></p></td>
          <td parentName="tr" {...{
            "align": "left"
          }}><p parentName="td">{`GET based on returned `}<inlineCode parentName="p">{`url`}</inlineCode></p></td>
          <td parentName="tr" {...{
            "align": "left"
          }}><p parentName="td">{`GET based on returned `}<inlineCode parentName="p">{`url`}</inlineCode></p></td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": "left"
          }}><p parentName="td"><a parentName="p" {...{
                "href": "/use-cases/apply-with-seek/populate-form"
              }}>{`Populate form`}</a></p></td>
          <td parentName="tr" {...{
            "align": "left"
          }}><p parentName="td">{`Partner pre-fills fields based on returned data`}</p></td>
          <td parentName="tr" {...{
            "align": "left"
          }}><p parentName="td">{`Partner pre-fills fields based on returned data`}</p></td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": "left"
          }}><p parentName="td"><a parentName="p" {...{
                "href": "/use-cases/apply-with-seek/send-application-complete-signal"
              }}>{`Send application complete signal`}</a></p></td>
          <td parentName="tr" {...{
            "align": "left"
          }}><p parentName="td">{`POST endpoint`}</p></td>
          <td parentName="tr" {...{
            "align": "left"
          }}><p parentName="td">{`GraphQL `}<a parentName="p" {...{
                "href": "/schema/mutation/sendApplicationSignal"
              }}><inlineCode parentName="a">{`sendApplicationSignal`}</inlineCode>{` mutation`}</a></p></td>
        </tr>
      </tbody>
    </table>
    <h2 {...{
      "id": "migration-considerations"
    }}>{`Migration considerations`}</h2>
    <h3 {...{
      "id": "linking-out-to-a-job-details-page"
    }}>{`Linking out to a job details page`}</h3>
    <p>{`When a candidate `}<a parentName="p" {...{
        "href": "/use-cases/job-posting/application-methods#linking-out-to-an-external-apply-form"
      }}>{`links out`}</a>{` from a SEEK job ad,
the new Apply with SEEK appends parameters to the query string of your `}<a parentName="p" {...{
        "href": "/schema/named-type/ApplicationMethod#/field/applicationUri"
      }}><inlineCode parentName="a">{`applicationUri`}</inlineCode></a>{`:`}</p>
    <ul>
      <li parentName="ul">
        <p parentName="li"><inlineCode parentName="p">{`seek-prefill-id`}</inlineCode>{` is optionally provided by the `}<a parentName="p" {...{
            "href": "/use-cases/apply-with-seek#candidate-experience"
          }}>{`pre-authorization flow`}</a>{` and should be used to pre-fill the apply form immediately`}</p>
      </li>
      <li parentName="ul">
        <p parentName="li"><inlineCode parentName="p">{`seek-token`}</inlineCode>{` should be persisted for application tracking and `}<a parentName="p" {...{
            "href": "/use-cases/apply-with-seek/send-application-complete-signal"
          }}>{`subsequent signals`}</a></p>
      </li>
    </ul>
    <p>{`These parameters are intended to be consumed by your apply form,
and require additional consideration if your `}<a parentName="p" {...{
        "href": "/schema/named-type/ApplicationMethod#/field/applicationUri"
      }}><inlineCode parentName="a">{`applicationUri`}</inlineCode></a>{` links to an intermediary job details page.`}</p>

    <h4 {...{
      "id": "option-1-point-applicationuri-directly-to-the-apply-form-recommended-"
    }}>{`Option 1: Point `}<inlineCode parentName="h4">{`applicationUri`}</inlineCode>{` directly to the apply form `}<Recommended mdxType="Recommended" /></h4>
    <img alt="" data-scoobie-style="none" height="32" src={require('../../../../mermaid/.2cabda69a16532fbfafbc1b37c73da4daba664de.mmd.svg')} title="" width="296" />
    <p>{`We recommend `}<a parentName="p" {...{
        "href": "/schema/named-type/ApplicationMethod#/field/applicationUri"
      }}><inlineCode parentName="a">{`applicationUri`}</inlineCode></a>{` to link directly to your apply form.
This provides candidates with a more seamless experience,
and keeps your job details page isolated from Apply with SEEK business logic.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-diff",
        "metastring": "label=\"applicationUri\"",
        "label": "\"applicationUri\""
      }}>{`- https://example.com/position/123
+ https://example.com/position/123/apply
`}</code></pre>
    <h4 {...{
      "id": "option-2-adapt-your-job-details-page-for-the-new-version"
    }}>{`Option 2: Adapt your job details page for the new version`}</h4>
    <img alt="" data-scoobie-style="none" height="32" src={require('../../../../mermaid/.e1b858e260151744905beaa0268580c9b4f36245.mmd.svg')} title="" width="555" />
    <p>{`If your software cannot link directly to the apply form,
all intermediary pages will need to propagate the `}<inlineCode parentName="p">{`seek-prefill-id`}</inlineCode>{` and `}<inlineCode parentName="p">{`seek-token`}</inlineCode>{` parameters through to the apply form.`}</p>
    <p>{`This option increases overall complexity as parameter handling will need to be maintained across multiple pages in your software.`}</p>
    <h3 {...{
      "id": "redirecting-to-an-auth-landing-page"
    }}>{`Redirecting to an auth landing page`}</h3>
    <p>{`With the previous version of Apply with SEEK,
each `}<inlineCode parentName="p">{`redirect_uri`}</inlineCode>{` had to be individually allowlisted via support request.
Some integrations adopted a centralised auth landing page to avoid repeating this manual process:`}</p>
    <img alt="" data-scoobie-style="none" height="32" src={require('../../../../mermaid/.d71e59d542539c44d8d2b5b1b19087793b0658ae.mmd.svg')} title="" width="505" />
    <p>{`For example,
your software may have implemented a landing page `}<inlineCode parentName="p">{`https://example.com/seek-auth`}</inlineCode>{` for use as the Apply with SEEK `}<inlineCode parentName="p">{`redirect_uri`}</inlineCode>{`.
Additional state like the apply form path `}<inlineCode parentName="p">{`/position/123/apply`}</inlineCode>{` could be packed into the `}<inlineCode parentName="p">{`state`}</inlineCode>{` parameter in the query string.
The landing page could then read the `}<inlineCode parentName="p">{`state`}</inlineCode>{` and use it to internally redirect to the final URL of the apply form.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-scoobie-merged-code",
        "metastring": "[{\"type\":\"code\",\"lang\":\"text\",\"meta\":\"label=\\\"SEEK authorization page\\\"\",\"value\":\"https://www.seek.com.au/api/iam/oauth2/authorize?redirect_uri=https%3A%2F%2Fexample.com%2Fseek-auth&state=%2Fposition%2F123%2Fapply&advertiser_id={advertiser_id}&client_id={client_id}&response_type=code&scope=r_profile_apply\",\"position\":{\"start\":{\"line\":140,\"column\":1,\"offset\":7511},\"end\":{\"line\":142,\"column\":4,\"offset\":7779},\"indent\":[1,1]}},{\"type\":\"code\",\"lang\":\"text\",\"meta\":\"label=\\\"Auth landing page\\\"\",\"value\":\"https://example.com/seek-auth?state=%2Fposition%2F123%2Fapply&code=abc123\",\"position\":{\"start\":{\"line\":144,\"column\":1,\"offset\":7781},\"end\":{\"line\":146,\"column\":4,\"offset\":7892},\"indent\":[1,1]}},{\"type\":\"code\",\"lang\":\"text\",\"meta\":\"label=\\\"Apply form\\\"\",\"value\":\"https://example.com/position/123/apply\",\"position\":{\"start\":{\"line\":148,\"column\":1,\"offset\":7894},\"end\":{\"line\":150,\"column\":4,\"offset\":7963},\"indent\":[1,1]}}]",
        "[{\"type\":\"code\",\"lang\":\"text\",\"meta\":\"label": "\\\"SEEK",
        "authorization": true,
        "page\\\"\",\"value\":\"https://www.seek.com.au/api/iam/oauth2/authorize?redirect_uri": "https%3A%2F%2Fexample.com%2Fseek-auth&state",
        "landing": true,
        "page\\\"\",\"value\":\"https://example.com/seek-auth?state": "%2Fposition%2F123%2Fapply&code",
        "form\\\"\",\"value\":\"https://example.com/position/123/apply\",\"position\":{\"start\":{\"line\":148,\"column\":1,\"offset\":7894},\"end\":{\"line\":150,\"column\":4,\"offset\":7963},\"indent\":[1,1]}}]": true
      }}>{`https://www.seek.com.au/api/iam/oauth2/authorize?redirect_uri=https%3A%2F%2Fexample.com%2Fseek-auth&state=%2Fposition%2F123%2Fapply&advertiser_id={advertiser_id}&client_id={client_id}&response_type=code&scope=r_profile_apply
`}</code></pre>
    <p>{`The new Apply with SEEK encourages your software to point `}<a parentName="p" {...{
        "href": "/schema/query/applyWithSeekButton#/argument/redirectUri"
      }}><inlineCode parentName="a">{`redirectUri`}</inlineCode></a>{` directly to the apply form.
It does away with the maintenance burden of strict URL allowlists,
though your software must follow `}<a parentName="p" {...{
        "href": "/use-cases/apply-with-seek/display-button#url-redirection"
      }}>{`best practices around URL redirection`}</a>{` to protect candidates from phishing attempts.`}</p>
    <p>{`If your software has an existing auth landing page,
you have two migration options when you upgrade to the new version.`}</p>
    <h4 {...{
      "id": "option-1-point-redirecturi-directly-to-the-apply-form-recommended-"
    }}>{`Option 1: Point `}<inlineCode parentName="h4">{`redirectUri`}</inlineCode>{` directly to the apply form `}<Recommended mdxType="Recommended" /></h4>
    <img alt="" data-scoobie-style="none" height="32" src={require('../../../../mermaid/.9c20846e961d3fa751f28cdf2289f5bcc6a96deb.mmd.svg')} title="" width="345" />
    <p>{`When a candidate authorizes access to their SEEK Profile,
your apply form will receive `}<inlineCode parentName="p">{`seek-prefill-id`}</inlineCode>{` and `}<inlineCode parentName="p">{`seek-token`}</inlineCode>{` parameters on the query string:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-text",
        "metastring": "label=\"apply form\"",
        "label": "\"apply",
        "form\"": true
      }}>{`https://example.com/position/123/apply?seek-prefill-id=def456&seek-token=ghi789
`}</code></pre>
    <p>{`This option allows your software to follow the `}<a parentName="p" {...{
        "href": "/use-cases/apply-with-seek/display-button"
      }}>{`simplified apply form model`}</a>{` from our standard `}<a parentName="p" {...{
        "href": "/use-cases/apply-with-seek"
      }}>{`use case documentation`}</a>{`.`}</p>
    <h4 {...{
      "id": "option-2-adapt-your-landing-page-for-the-new-version"
    }}>{`Option 2: Adapt your landing page for the new version`}</h4>
    <img alt="" data-scoobie-style="none" height="32" src={require('../../../../mermaid/.d71e59d542539c44d8d2b5b1b19087793b0658ae.mmd.svg')} title="" width="505" />
    <p>{`While the new Apply with SEEK does not explicitly manage the `}<inlineCode parentName="p">{`state`}</inlineCode>{` parameter,
your software can include a query string in the `}<a parentName="p" {...{
        "href": "/schema/query/applyWithSeekButton#/argument/redirectUri"
      }}><inlineCode parentName="a">{`redirectUri`}</inlineCode></a>{`:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-scoobie-merged-code",
        "metastring": "[{\"type\":\"code\",\"lang\":\"graphql\",\"meta\":null,\"value\":\"query ($input: ApplyWithSeekButtonInput!) {\\n  applyWithSeekButton(input: $input) {\\n    buttonLabel\\n    images {\\n      accent\\n    }\\n    url\\n  }\\n}\",\"position\":{\"start\":{\"line\":193,\"column\":1,\"offset\":9495},\"end\":{\"line\":203,\"column\":4,\"offset\":9654},\"indent\":[1,1,1,1,1,1,1,1,1,1]}},{\"type\":\"code\",\"lang\":\"jsonc\",\"meta\":\"default\",\"value\":\"{\\n  \\\"input\\\": {\\n    // The HTTPS URL to redirect the candidate to after authorization.\\n    // Your software may embed dynamic state in the query string for CSRF protection.\\n    \\\"redirectUri\\\": \\\"https://example.com/seek-auth?state=%2Fposition%2F123%2Fapply\\\",\\n\\n    // The identifier for the SEEK hirer that is filling the position.\\n    // \\\"<Partner Name> on behalf of <Hirer Name>\\\" will be displayed on the authorization page.\\n    \\\"hirerId\\\": \\\"seekAnzPublicTest:organization:seek:93WyyF1h\\\",\\n\\n    // The HTTPS URL of your apply form for the specific position.\\n    \\\"applicationUri\\\": \\\"https://example.com/position/123/apply\\\",\\n\\n    // An optional hirer-provided free-text job reference.\\n    \\\"seekHirerJobReference\\\": \\\"REQ-65558\\\",\\n\\n    // The seek-token received as part of the query string when the candidate arrived from SEEK.\\n    \\\"token\\\": \\\"eyJqb2JJZCI6MCwiY2FuZGlkYXRlSWQiOjAsInNlc3Npb25JZCI6IiJ9\\\"\\n  }\\n}\",\"position\":{\"start\":{\"line\":205,\"column\":1,\"offset\":9656},\"end\":{\"line\":226,\"column\":4,\"offset\":10572},\"indent\":[1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1]}},{\"type\":\"code\",\"lang\":\"json\",\"meta\":null,\"value\":\"{\\n  \\\"applyWithSeekButton\\\": {\\n    \\\"buttonLabel\\\": \\\"Apply with SEEK\\\",\\n    \\\"images\\\": {\\n      \\\"accent\\\": \\\"https://integration.seek.com/awsk/button-images/seek/en/accent.svg\\\"\\n    },\\n    \\\"url\\\": \\\"https://www.seek.com.au/awsk/authorize?id=00000000-0000-4000-8000-000000000000\\\"\\n  }\\n}\",\"position\":{\"start\":{\"line\":228,\"column\":1,\"offset\":10574},\"end\":{\"line\":238,\"column\":4,\"offset\":10858},\"indent\":[1,1,1,1,1,1,1,1,1,1]}}]",
        "[{\"type\":\"code\",\"lang\":\"graphql\",\"meta\":null,\"value\":\"query": true,
        "($input:": true,
        "ApplyWithSeekButtonInput!)": true,
        "{\\n": true,
        "": true,
        "applyWithSeekButton(input:": true,
        "$input)": true,
        "buttonLabel\\n": true,
        "images": true,
        "accent\\n": true,
        "}\\n": true,
        "url\\n": true,
        "}\\n}\",\"position\":{\"start\":{\"line\":193,\"column\":1,\"offset\":9495},\"end\":{\"line\":203,\"column\":4,\"offset\":9654},\"indent\":[1,1,1,1,1,1,1,1,1,1]}},{\"type\":\"code\",\"lang\":\"jsonc\",\"meta\":\"default\",\"value\":\"{\\n": true,
        "\\\"input\\\":": true,
        "//": true,
        "The": true,
        "HTTPS": true,
        "URL": true,
        "to": true,
        "redirect": true,
        "the": true,
        "candidate": true,
        "after": true,
        "authorization.\\n": true,
        "Your": true,
        "software": true,
        "may": true,
        "embed": true,
        "dynamic": true,
        "state": true,
        "in": true,
        "query": true,
        "string": true,
        "for": true,
        "CSRF": true,
        "protection.\\n": true,
        "\\\"redirectUri\\\":": true,
        "\\\"https://example.com/seek-auth?state": "%2Fposition%2F123%2Fapply\\\",\\n\\n",
        "identifier": true,
        "SEEK": true,
        "hirer": true,
        "that": true,
        "is": true,
        "filling": true,
        "position.\\n": true,
        "\\\"<Partner": true,
        "Name>": true,
        "on": true,
        "behalf": true,
        "of": true,
        "<Hirer": true,
        "Name>\\\"": true,
        "will": true,
        "be": true,
        "displayed": true,
        "authorization": true,
        "page.\\n": true,
        "\\\"hirerId\\\":": true,
        "\\\"seekAnzPublicTest:organization:seek:93WyyF1h\\\",\\n\\n": true,
        "your": true,
        "apply": true,
        "form": true,
        "specific": true,
        "\\\"applicationUri\\\":": true,
        "\\\"https://example.com/position/123/apply\\\",\\n\\n": true,
        "An": true,
        "optional": true,
        "hirer-provided": true,
        "free-text": true,
        "job": true,
        "reference.\\n": true,
        "\\\"seekHirerJobReference\\\":": true,
        "\\\"REQ-65558\\\",\\n\\n": true,
        "seek-token": true,
        "received": true,
        "as": true,
        "part": true,
        "when": true,
        "arrived": true,
        "from": true,
        "SEEK.\\n": true,
        "\\\"token\\\":": true,
        "\\\"eyJqb2JJZCI6MCwiY2FuZGlkYXRlSWQiOjAsInNlc3Npb25JZCI6IiJ9\\\"\\n": true,
        "}\\n}\",\"position\":{\"start\":{\"line\":205,\"column\":1,\"offset\":9656},\"end\":{\"line\":226,\"column\":4,\"offset\":10572},\"indent\":[1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1]}},{\"type\":\"code\",\"lang\":\"json\",\"meta\":null,\"value\":\"{\\n": true,
        "\\\"applyWithSeekButton\\\":": true,
        "\\\"buttonLabel\\\":": true,
        "\\\"Apply": true,
        "with": true,
        "SEEK\\\",\\n": true,
        "\\\"images\\\":": true,
        "\\\"accent\\\":": true,
        "\\\"https://integration.seek.com/awsk/button-images/seek/en/accent.svg\\\"\\n": true,
        "},\\n": true,
        "\\\"url\\\":": true,
        "\\\"https://www.seek.com.au/awsk/authorize?id": "00000000-0000-4000-8000-000000000000\\\"\\n",
        "}\\n}\",\"position\":{\"start\":{\"line\":228,\"column\":1,\"offset\":10574},\"end\":{\"line\":238,\"column\":4,\"offset\":10858},\"indent\":[1,1,1,1,1,1,1,1,1,1]}}]": true
      }}>{`query ($input: ApplyWithSeekButtonInput!) {
  applyWithSeekButton(input: $input) {
    buttonLabel
    images {
      accent
    }
    url
  }
}
`}</code></pre>
    <p>{`Your auth landing page will receive `}<inlineCode parentName="p">{`seek-prefill-id`}</inlineCode>{` and `}<inlineCode parentName="p">{`seek-token`}</inlineCode>{` parameters on the query string,
and must propagate them to your apply form for pre-fill and application tracking:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-scoobie-merged-code",
        "metastring": "[{\"type\":\"code\",\"lang\":\"text\",\"meta\":\"label=\\\"Auth landing page\\\"\",\"value\":\"https://example.com/seek-auth?state=%2Fposition%2F123%2Fapply&seek-prefill-id=def456&seek-token=ghi789\",\"position\":{\"start\":{\"line\":243,\"column\":1,\"offset\":11046},\"end\":{\"line\":245,\"column\":4,\"offset\":11186},\"indent\":[1,1]}},{\"type\":\"code\",\"lang\":\"text\",\"meta\":\"label=\\\"Apply form\\\"\",\"value\":\"https://example.com/position/123/apply&seek-prefill-id=def456&seek-token=ghi789\",\"position\":{\"start\":{\"line\":247,\"column\":1,\"offset\":11188},\"end\":{\"line\":249,\"column\":4,\"offset\":11298},\"indent\":[1,1]}}]",
        "[{\"type\":\"code\",\"lang\":\"text\",\"meta\":\"label": "\\\"Auth",
        "landing": true,
        "page\\\"\",\"value\":\"https://example.com/seek-auth?state": "%2Fposition%2F123%2Fapply&seek-prefill-id",
        "form\\\"\",\"value\":\"https://example.com/position/123/apply&seek-prefill-id": "def456&seek-token"
      }}>{`https://example.com/seek-auth?state=%2Fposition%2F123%2Fapply&seek-prefill-id=def456&seek-token=ghi789
`}</code></pre>
    <p>{`This option increases overall complexity as parameter handling will need to be maintained across multiple pages in your software.`}</p>
    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;